<template>
  <v-dialog :value="value" persistent max-width="480" @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="headline">
        Verificar celular (WhatsApp)
      </v-card-title>
      <v-card-subtitle v-if="pendientes.length > 1" class="pb-0">
        Alumno {{ indiceActual + 1 }} de {{ pendientes.length }}
      </v-card-subtitle>
      <v-card-text>
        <v-alert type="info" dense text class="mb-3">
          Enviaremos un código al WhatsApp ligado al prospecto.
          Debes validarlo para continuar con la inscripción.
        </v-alert>

        <div v-if="actual" class="mb-3">
          <div class="subtitle-1 font-weight-medium">{{ actual.nombre || 'Prospecto' }}</div>
          <div class="caption grey--text">
            Folio / id: {{ actual.id_prospecto || '—' }}
          </div>
          <div class="body-2 mt-1">
            Celular:
            <b>{{ telefonoMascara || enmascararLocal(actual.celular) }}</b>
          </div>
        </div>

        <v-text-field
          v-model="codigo"
          label="Código de 6 dígitos"
          type="tel"
          maxlength="6"
          filled
          dense
          :disabled="!codigoEnviado || verificando"
          :error-messages="errorCodigo"
          @keyup.enter="verificar"
        />

        <div v-if="mensajeInfo" class="caption success--text mb-2">{{ mensajeInfo }}</div>
        <div v-if="errorGeneral" class="caption error--text mb-2">{{ errorGeneral }}</div>
      </v-card-text>

      <v-card-actions>
        <v-btn text :disabled="enviando || verificando" @click="cancelar">
          Cancelar
        </v-btn>
        <v-spacer />
        <v-btn
          text
          color="primary"
          :loading="enviando"
          :disabled="verificando || cooldownRestante > 0"
          @click="enviar"
        >
          <span v-if="cooldownRestante > 0">Reenviar ({{ cooldownRestante }}s)</span>
          <span v-else-if="codigoEnviado">Reenviar código</span>
          <span v-else>Enviar código</span>
        </v-btn>
        <v-btn
          color="primary"
          :loading="verificando"
          :disabled="!codigoEnviado || !codigo || codigo.length < 6 || enviando"
          @click="verificar"
        >
          Validar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'DialogOtpWhatsApp',

  props: {
    value: { type: Boolean, default: false },
    /** Lista de { nombre, celular, id_prospecto, key? } a verificar uno por uno */
    destinatarios: { type: Array, default: () => [] },
  },

  data: () => ({
    indiceActual: 0,
    codigo: '',
    codigoEnviado: false,
    enviando: false,
    verificando: false,
    telefonoMascara: '',
    errorCodigo: '',
    errorGeneral: '',
    mensajeInfo: '',
    cooldownRestante: 0,
    _cooldownTimer: null,
  }),

  computed: {
    ...mapGetters('login', ['getEscuela']),
    pendientes() {
      return (this.destinatarios || []).filter((d) => d && d.celular)
    },
    actual() {
      return this.pendientes[this.indiceActual] || null
    },
  },

  watch: {
    value(abierto) {
      if (abierto) {
        this.resetFlujo()
        this.$nextTick(() => {
          if (this.actual) this.enviar()
        })
      } else {
        this.limpiarCooldown()
      }
    },
  },

  beforeDestroy() {
    this.limpiarCooldown()
  },

  methods: {
    enmascararLocal(tel) {
      const d = String(tel || '').replace(/\D/g, '').slice(-10)
      if (d.length !== 10) return '****'
      return `******${d.slice(-4)}`
    },

    resetFlujo() {
      this.indiceActual = 0
      this.resetPaso()
    },

    resetPaso() {
      this.codigo = ''
      this.codigoEnviado = false
      this.enviando = false
      this.verificando = false
      this.telefonoMascara = ''
      this.errorCodigo = ''
      this.errorGeneral = ''
      this.mensajeInfo = ''
      this.limpiarCooldown()
    },

    limpiarCooldown() {
      if (this._cooldownTimer) {
        clearInterval(this._cooldownTimer)
        this._cooldownTimer = null
      }
      this.cooldownRestante = 0
    },

    iniciarCooldown(segundos) {
      this.limpiarCooldown()
      this.cooldownRestante = Math.max(0, Number(segundos) || 0)
      if (this.cooldownRestante <= 0) return
      this._cooldownTimer = setInterval(() => {
        this.cooldownRestante -= 1
        if (this.cooldownRestante <= 0) this.limpiarCooldown()
      }, 1000)
    },

    cancelar() {
      this.$emit('input', false)
      this.$emit('cancelado')
    },

    async enviar() {
      if (!this.actual) {
        this.errorGeneral = 'No hay destinatario para verificar'
        return
      }
      this.enviando = true
      this.errorGeneral = ''
      this.errorCodigo = ''
      this.mensajeInfo = ''
      try {
        const { data } = await this.$http.post('inscripciones.otp.enviar', {
          telefono: this.actual.celular,
          unidad_negocio: this.getEscuela,
          id_prospecto: this.actual.id_prospecto,
          nombre: this.actual.nombre,
        })
        this.codigoEnviado = true
        this.telefonoMascara = (data && data.telefono_mascara) || this.enmascararLocal(this.actual.celular)
        this.mensajeInfo = `Código enviado por WhatsApp a ${this.telefonoMascara}`
        this.iniciarCooldown((data && data.resend_in) || 60)
      } catch (error) {
        const body = error && error.response && error.response.data
        this.errorGeneral = (body && body.message) || 'No se pudo enviar el código'
        if (body && body.wait_sec) this.iniciarCooldown(body.wait_sec)
      } finally {
        this.enviando = false
      }
    },

    async verificar() {
      if (!this.actual || !this.codigo) return
      this.verificando = true
      this.errorCodigo = ''
      this.errorGeneral = ''
      try {
        await this.$http.post('inscripciones.otp.verificar', {
          telefono: this.actual.celular,
          codigo: this.codigo,
          unidad_negocio: this.getEscuela,
          id_prospecto: this.actual.id_prospecto,
        })
        this.$emit('verificado', { ...this.actual })

        if (this.indiceActual < this.pendientes.length - 1) {
          this.indiceActual += 1
          this.resetPaso()
          this.mensajeInfo = 'Código correcto. Continúa con el siguiente alumno.'
          await this.enviar()
        } else {
          this.$emit('input', false)
          this.$emit('completado')
        }
      } catch (error) {
        const body = error && error.response && error.response.data
        this.errorCodigo = (body && body.message) || 'Código incorrecto'
      } finally {
        this.verificando = false
      }
    },
  },
}
</script>
